import { useEffect, useState } from "react";
import { Sparkles, Loader2, CalendarPlus, Check, AlertCircle } from "lucide-react";
import { listCompanies, suggestCompliance, applyComplianceSuggestions } from "@/lib/api";
import { toast } from "sonner";

export default function ComplianceSuggestPanel() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});
  const [meta, setMeta] = useState({});

  useEffect(() => {
    listCompanies().then((r) => {
      const list = r.data || [];
      setCompanies(list);
      if (list.length === 1) setCompanyId(list[0].id);
    });
  }, []);

  const fetchSuggestions = async () => {
    if (!companyId) {
      toast.error("Select a company first");
      return;
    }
    setLoading(true);
    setSuggestions([]);
    setSelected({});
    try {
      const r = await suggestCompliance(companyId, 8);
      const items = r.data?.suggestions || [];
      setSuggestions(items);
      setMeta({ fy: r.data?.fy, today: r.data?.today });
      const initial = {};
      items.forEach((_, idx) => (initial[idx] = true));
      setSelected(initial);
      if (items.length === 0) toast.message("No actionable suggestions found");
      else toast.success(`${items.length} AI suggestions ready`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to fetch suggestions");
    } finally {
      setLoading(false);
    }
  };

  const applyToCalendar = async () => {
    const picked = suggestions.filter((_, idx) => selected[idx]);
    if (picked.length === 0) {
      toast.error("Select at least one suggestion");
      return;
    }
    setApplying(true);
    try {
      const r = await applyComplianceSuggestions(companyId, picked);
      toast.success(`Added ${r.data?.created} event(s) to Compliance Calendar${r.data?.skipped ? ` (${r.data.skipped} skipped — already exist)` : ""}`);
      setSuggestions([]);
      setSelected({});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply suggestions");
    } finally {
      setApplying(false);
    }
  };

  const allChecked = suggestions.length > 0 && suggestions.every((_, i) => selected[i]);
  const toggleAll = () => {
    const next = {};
    suggestions.forEach((_, i) => (next[i] = !allChecked));
    setSelected(next);
  };

  return (
    <div className="border border-zinc-200 bg-white p-6 mb-8" data-testid="suggest-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={16} className="text-[#002FA7]" />
            <h3 className="font-heading text-lg font-medium tracking-tight">AI-Suggested Compliance Actions</h3>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed max-w-2xl">
            Claude Sonnet 4.5 reads the latest MCA / SAG Infotech notifications + your company profile (capital, directors, type) and proposes the most relevant compliance items with statutory due dates. One click adds them to your calendar.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="border border-zinc-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            data-testid="suggest-company-select"
          >
            <option value="">Select company…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.company_type})</option>
            ))}
          </select>
          <button
            onClick={fetchSuggestions}
            disabled={loading || !companyId}
            className="swiss-btn-primary"
            data-testid="suggest-fetch-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {loading ? "Analysing…" : "Suggest Actions"}
          </button>
        </div>
      </div>

      {companies.length === 0 && (
        <div className="p-3 border border-orange-300 bg-orange-50 text-xs flex gap-2 items-center" data-testid="suggest-no-companies">
          <AlertCircle size={14} className="text-orange-600" />
          Add a company first to receive compliance suggestions.
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="mt-2 border-t border-zinc-200 pt-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">
              {suggestions.length} suggestions · FY {meta.fy} · as of {meta.today}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={toggleAll} className="text-xs text-zinc-600 hover:text-[#002FA7] flex items-center gap-1" data-testid="suggest-toggle-all">
                <Check size={12} /> {allChecked ? "Deselect all" : "Select all"}
              </button>
              <button
                onClick={applyToCalendar}
                disabled={applying}
                className="swiss-btn-primary"
                data-testid="suggest-apply-btn"
              >
                {applying ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
                {applying ? "Adding…" : "Add Selected to Calendar"}
              </button>
            </div>
          </div>
          <ul className="space-y-2" data-testid="suggest-list">
            {suggestions.map((s, idx) => (
              <li
                key={`${s.title}-${s.due_date}-${idx}`}
                className="border border-zinc-200 p-3 hover:border-[#002FA7] transition-colors flex gap-3"
                data-testid={`suggest-item-${idx}`}
              >
                <input
                  type="checkbox"
                  checked={!!selected[idx]}
                  onChange={() => setSelected({ ...selected, [idx]: !selected[idx] })}
                  className="accent-[#002FA7] mt-1"
                  data-testid={`suggest-check-${idx}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap mb-1">
                    <div className="font-medium text-sm">{s.title}</div>
                    {s.due_date && (
                      <span className="text-[10px] uppercase tracking-[0.15em] text-[#002FA7] font-mono-plex">
                        Due {s.due_date}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 mb-1 flex-wrap">
                    {s.category && <span>{s.category}</span>}
                    {s.section && <><span>·</span><span>Sec {s.section}</span></>}
                    {s.form && <><span>·</span><span>{s.form}</span></>}
                  </div>
                  {s.description && (
                    <p className="text-xs text-zinc-700 leading-relaxed">{s.description}</p>
                  )}
                  {s.rationale && (
                    <p className="text-[11px] text-zinc-500 mt-1 italic">Why: {s.rationale}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
